/**
 * Инлайн-SVG иконки внутреннего портала (в проекте нет icon-библиотек).
 *
 * Стиль и размерность совпадают с `components/client/clientIcons.tsx` — outline,
 * 24×24, `currentColor`, толщина 1.8. Здесь лежат только те иконки, которых в
 * клиентском наборе нет; общие (Home, News, Truck, Chart, User, Search, Bell,
 * ChevronRight, Box, Document) переиспользуются оттуда, а не дублируются.
 */
type IconProps = { size?: number; color?: string };

function base(size: number | undefined) {
  return {
    width: size ?? 24,
    height: size ?? 24,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
}

/** Рабочий стол — сетка 2×2. */
export function MicIcon({ size, color }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color ?? 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3M8 21h8" />
    </svg>
  );
}

export function GridIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </svg>
  );
}

/** Контрагенты — здание организации. */
export function BuildingIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M3 21h18" />
      <path d="M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16" />
      <path d="M15 21V10h3a2 2 0 0 1 2 2v9" />
      <path d="M8 7h4M8 11h4M8 15h4" />
    </svg>
  );
}

/**
 * Дебиторская задолженность — чек с рублём.
 * Знак валюты внутри: без него иконка неотличима от «документа»/«новостей».
 */
export function ReceiptIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M5 3h14v18l-2.5-1.6L14 21l-2-1.6L10 21l-2.5-1.6L5 21V3Z" />
      <path d="M10 7.5h2.4a2.1 2.1 0 0 1 0 4.2H10V7.5Z" />
      <path d="M10 11.7v4.1M8.6 13.9h3.4" />
    </svg>
  );
}

/** Остатки на складах — склад с воротами. */
export function WarehouseIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M3 10.5 12 4l9 6.5V21H3V10.5Z" />
      <path d="M9 21v-6h6v6" />
    </svg>
  );
}

/** Коммерческие условия — ценник. */
export function TagIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M3 12.5V4a1 1 0 0 1 1-1h8.5L21 11.5 12.5 20 3 12.5Z" />
      <circle cx="7.5" cy="7.5" r="1.4" />
    </svg>
  );
}

/** Клиенты — группа людей. */
export function UsersIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
      <path d="M16 5.3a3.2 3.2 0 0 1 0 5.9" />
      <path d="M18 14.9c2 .8 3 2.5 3 5.1" />
    </svg>
  );
}

/** Товары — коробка. */
export function PackageIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M12 3 3 7.5v9L12 21l9-4.5v-9L12 3Z" />
      <path d="M3 7.5 12 12l9-4.5" />
      <path d="M12 12v9" />
    </svg>
  );
}

/** Производители — завод. */
export function FactoryIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M3 21V10l5.5 3.5V10L14 13.5V10l5.5 3.5V21H3Z" />
      <path d="M6 6h3v4" />
    </svg>
  );
}

/** Поставщики — витрина. */
export function StoreIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M4 9h16v12H4V9Z" />
      <path d="M3 9l1.6-5h14.8L21 9" />
      <path d="M8.5 21v-6h7v6" />
    </svg>
  );
}

/** Один пользователь — для «Пользователи». */
export function UserSingleIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
    </svg>
  );
}

/** Машины — легковой автомобиль. */
export function CarIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M4 16v-3l2-5h12l2 5v3" />
      <path d="M3 16h18v3h-3v-3M6 19H3v-3" />
      <circle cx="7.5" cy="16.5" r="1.6" />
      <circle cx="16.5" cy="16.5" r="1.6" />
    </svg>
  );
}

/** Доп. услуги — плюс в круге. */
export function ServiceIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 8.5v7M8.5 12h7" />
    </svg>
  );
}

/** Типы заявок — маркированный список. */
export function ListIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <circle cx="4.5" cy="6" r="1.3" />
      <circle cx="4.5" cy="12" r="1.3" />
      <circle cx="4.5" cy="18" r="1.3" />
    </svg>
  );
}

/** Товарные группы — слои. */
export function GroupsIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M12 3 3 7.5 12 12l9-4.5L12 3Z" />
      <path d="m3 12.5 9 4.5 9-4.5" />
      <path d="m3 17 9 4.5 9-4.5" />
    </svg>
  );
}

/** Юрлица — документ с печатью. */
export function LegalIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M6 3h8l4 4v14H6V3Z" />
      <path d="M14 3v4h4" />
      <circle cx="12" cy="14" r="2.4" />
    </svg>
  );
}

/** Выход из системы. */
export function LogoutIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
      <path d="M10 8.5 6.5 12l3.5 3.5" />
      <path d="M6.5 12H16" />
    </svg>
  );
}

/** Меню-бургер для мобильного каркаса. */
export function MenuIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

/** Удаление — корзина. */
export function TrashIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M4 7h16" />
      <path d="M9.5 7V5.2A1.2 1.2 0 0 1 10.7 4h2.6a1.2 1.2 0 0 1 1.2 1.2V7" />
      <path d="M6.5 7 7.4 19a1.5 1.5 0 0 0 1.5 1.4h6.2a1.5 1.5 0 0 0 1.5-1.4L17.5 7" />
      <path d="M10.5 11v5.5M13.5 11v5.5" />
    </svg>
  );
}

/** Заявки на согласование — документ с галочкой. */
export function InboxCheckIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5.5Z" />
      <path d="m8.5 12 2.5 2.5 4.5-4.5" />
    </svg>
  );
}

/** ИИ аналитик — искры. */
export function AiIcon({ size, color }: IconProps) {
  return (
    <svg {...base(size)} style={color ? { color } : undefined}>
      <path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" />
      <path d="M18 15l.8 2.2L21 18l-2.2.8L18 21l-.8-2.2L15 18l2.2-.8z" />
    </svg>
  );
}
